import { useEffect, useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppSelector } from 'redux/hooks';

import { CategoryResponse, categoryDataService } from 'services/category.service';
import { notificationDataService } from 'services/notification.service';

import { useFetch } from 'hooks';

import { mapSeasons } from 'helpers/mapSeasons';

import { useUsers } from 'pages/Users/Users.hooks';

import { EditForm, FormItem } from 'sections/EditForm';
import { SectionType } from 'sections/TabWrap';

const useData = () => {
  useUsers();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const seasonsData = useAppSelector((state) => state.constants.seasons);

  const seasonOptions = useMemo(() => {
    const seasonsToObject = mapSeasons(seasonsData);
    return seasonsToObject.map((s) => ({ value: s.season, label: s.season }));
  }, [seasonsData]);

  const seasonLineOptions = useMemo(() => {
    const seasonsToObject = mapSeasons(seasonsData);
    const allLines = seasonsToObject.flatMap((s) => s.catalog);
    const uniqueLines = Array.from(new Set(allLines));
    return uniqueLines.map((line) => ({ value: line, label: line }));
  }, [seasonsData]);

  const { data: categoriesData } = useFetch(() => categoryDataService.getAll(), []);

  const categoryOptions = useMemo(() => {
    if (!categoriesData) return [];
    return (categoriesData as CategoryResponse[]).map((cat) => ({
      value: cat.id,
      label: cat.nameEn || cat.nameIt,
    }));
  }, [categoriesData]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting, errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      season: '',
      seasonLine: '',
      categoryId: '',
      titleIt: '',
      titleEn: '',
      shortDescriptionIt: '',
      shortDescriptionEn: '',
      descriptionIt: '',
      descriptionEn: '',
      isPublished: false,
      products: undefined,
    },
  });

  const { data: notificationData } = useFetch(
    id ? () => notificationDataService.getById(id) : undefined,
    [id]
  );

  useEffect(() => {
    if (notificationData) {
      reset({
        season: notificationData.season ?? '',
        seasonLine: notificationData.seasonLine ?? '',
        categoryId: notificationData.category?.id ?? notificationData.categoryId ?? '',
        titleIt: notificationData.title?.it ?? '',
        titleEn: notificationData.title?.en ?? '',
        shortDescriptionIt: notificationData.shortDescription?.it ?? '',
        shortDescriptionEn: notificationData.shortDescription?.en ?? '',
        descriptionIt: notificationData.description?.it ?? '',
        descriptionEn: notificationData.description?.en ?? '',
        isPublished: !!notificationData.isPublished,
        products: undefined,
      });
    }
  }, [notificationData, reset]);

  const onSubmit = (formValues: FieldValues) => {
    const notificationPostRequestDto = {
      season: formValues.season,
      seasonLine: formValues.seasonLine,
      categoryId: formValues.categoryId || undefined,
      italianTitle: formValues.titleIt,
      englishTitle: formValues.titleEn,
      italianShortDescription: formValues.shortDescriptionIt,
      englishShortDescription: formValues.shortDescriptionEn,
      italianDescription: formValues.descriptionIt,
      englishDescription: formValues.descriptionEn,
      isPublished: !!formValues.isPublished,
    };

    const productsFile =
      formValues.products && formValues.products.length > 0
        ? formValues.products[0]
        : undefined;

    const serviceCall = id
      ? notificationDataService.edit(notificationPostRequestDto, productsFile, id)
      : notificationDataService.create(notificationPostRequestDto, productsFile);

    serviceCall
      .then(() => {
        toast.success(
          id ? 'Notification edited successfully!' : 'Notification created successfully!'
        );
        navigate('/catalogue/notifications');
      })
      .catch((err) => {
        toast.error(err?.message || 'An error occurred');
      });
  };

  const data: FormItem[] = [
    {
      name: 'season',
      value: notificationData?.season ?? '',
      label: 'Season',
      type: 'select',
      options: seasonOptions,
      required: true,
    },
    {
      name: 'seasonLine',
      value: notificationData?.seasonLine ?? '',
      label: 'Season Line',
      type: 'select',
      options: seasonLineOptions,
      required: true,
    },
    {
      name: 'categoryId',
      value: notificationData?.category?.id ?? notificationData?.categoryId ?? '',
      label: 'Category',
      type: 'select',
      options: categoryOptions,
    },
    {
      name: 'titleIt',
      value: notificationData?.title?.it ?? '',
      label: 'Title IT',
      flag: 'it',
      required: true,
    },
    {
      name: 'titleEn',
      value: notificationData?.title?.en ?? '',
      label: 'Title EN',
      flag: 'en',
      required: true,
    },
    {
      name: 'shortDescriptionIt',
      value: notificationData?.shortDescription?.it ?? '',
      label: 'Short Description IT',
      flag: 'it',
      required: true,
    },
    {
      name: 'shortDescriptionEn',
      value: notificationData?.shortDescription?.en ?? '',
      label: 'Short Description EN',
      flag: 'en',
      required: true,
    },
    {
      name: 'descriptionIt',
      value: notificationData?.description?.it ?? '',
      label: 'Description IT',
      flag: 'it',
      required: true,
    },
    {
      name: 'descriptionEn',
      value: notificationData?.description?.en ?? '',
      label: 'Description EN',
      flag: 'en',
      required: true,
    },
    {
      name: 'isPublished',
      value: notificationData?.isPublished ?? false,
      label: 'Published',
      type: 'checkboxWithLabel',
    },
    {
      name: 'products',
      label: 'Products (file)',
      type: 'file',
    },
  ];

  const sections: SectionType[] = [
    {
      id: 1,
      content: (
        <EditForm
          onSubmit={handleSubmit(onSubmit)}
          data={data}
          register={register as any}
          control={control as any}
          hideUndo
          hasButtons
          submitBtnLabel={id ? 'Save' : 'Submit'}
          isDirty
          isButtonsDisabled={!isValid || isSubmitting}
        />
      ),
    },
  ];

  return {
    sections,
  };
};

export { useData };
